"use client";

import ScrollReveal from "@/components/common/ScrollReveal";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import React, { useEffect, useId, useRef, useState } from "react";
import {
  ACCORDION_PANEL_BG,
  AccordionChevron,
  accordionBody,
  accordionBodyClosed,
  accordionBodyOpen,
  accordionCard,
  accordionDivider,
  accordionHeader,
  accordionPanel,
  accordionPanelClip,
  accordionPanelClosed,
  accordionPanelOpen,
  useAccordionScrollAnchor,
} from "@/components/common/accordion";

type AptStep = {
  id: string;
  name: string;
  value: string;
  description: string;
};

const APT_STEPS: AptStep[] = [
  {
    id: "1",
    name: "Recon",
    value: "recon",
    description:
      "ECQ needs to select the target and clearly define the objectives. A considerable amount of reconnaissance works such as social engineering are expected in this phase. ECQ would also need to find out the security controls that they might face and create evasion and response plan in the event something goes awry.",
  },
  {
    id: "2",
    name: "Prepare",
    value: "prepare",
    description:
      "ECQ may acquire, build tools and implants that are specific to the targets and objectives. Preparation for an attack normally also involves with security research to discover zero-day vulnerability (undisclosed security issue) in software or hardware used by the target organization or develop 1-day vulnerability (freshly released security issue).",
  },
  {
    id: "3",
    name: "Deploy",
    value: "deploy",
    description:
      "After completing all the necessary reconnaissance and preparation, ECQ delivers the artifact and dropper to the target infrastructure via spear phishing, USB, watering-hole, or simply exploiting vulnerable public services such as web services or applications and mail server.",
  },
  {
    id: "4",
    name: "Exploit",
    value: "exploit",
    description:
      "ECQ transfers additional tools and exploit code to the initial compromised target in an attempt to escalate privilege to become root or administrator and maintain persistent access to the target. Techniques such as implanting rootkit, capturing input, modifying registry and startup services, and process binding are normally executed during this phase.",
  },
  {
    id: "5",
    name: "Expand",
    value: "expand",
    description:
      "Expansion or lateral movement is perhaps one of the most important phases in an APT attack where ECQ tries to expand controls of the target network in search of valuable information. The initial compromised system can be used as a proxy to help the Consultant further infiltrate the network. ECQ will also attempt to exploit and maintain persistence over strategic servers or network devices that can be used to initiate outbound connection, store and transfer data, or simply sniff for inbound and outbound data.",
  },
  {
    id: "6",
    name: "Command & Control",
    value: "command-control",
    description:
      "ECQ's Command & Control (C2) infrastructure helps establish and maintain connectivity with the implants inside the target network. Communication with ECQ's C2 server is established over multiple channels and protocols such as HTTPS, ICMP, DNS, Twitter, and many more. Deployed implants can operate in interactive and non-interactive mode with the C2. In cases where Internet access is limited or connectivity with C2 is not possible, ECQ's implant can also work in P2P mode.",
  },
  {
    id: "7",
    name: "Impact",
    value: "impact",
    description:
      "Depends on the predefined objective with the organization, ECQ will proceed to simulate impact of an APT attack to illustrate true risk in the event an adversary were able to reach to this stage.\n\nImpacts simulated by ECQ include exfiltration of sensitive financial or secret data, create a fake financial transaction from the Core Banking or SWIFT zone, or disrupt an operation of an ICS/SCADA plant.",
  },
];

// APT diagram ring — positions replicate the V1 build exactly
// (.adversary-apt__circle:nth-child(n) in e-cq.net/assets/css/style.css).
// top/left are relative to the ring box (.apt-diagram__ring).
const DIAGRAM_CIRCLES: {
  label: string;
  value: string;
  top: string;
  left: string;
}[] = [
  { label: "Recon", value: "recon", top: "-7%", left: "54%" },
  { label: "Prepare", value: "prepare", top: "9%", left: "95%" },
  { label: "Deploy", value: "deploy", top: "45%", left: "109%" },
  { label: "Exploit", value: "exploit", top: "76%", left: "88%" },
  { label: "Expand", value: "expand", top: "78%", left: "32%" },
  { label: "C2", value: "command-control", top: "45%", left: "-5%" },
  { label: "Impact", value: "impact", top: "9%", left: "9%" },
];

const AdversarySimulation = () => {
  const [activeTab, setActiveTab] = useState("recon");
  const uid = useId();
  // Only used for `aria-pressed` on the ring, which has to name one of the two
  // selection models; the red ring itself is chosen in CSS.
  const isDesktop = useIsDesktop();
  // Below lg: which phase row is expanded. Separate from `activeTab` (and null
  // at first, i.e. every row collapsed) so the desktop panel and the APT ring
  // keep a phase selected — same contract as TabbedContent / the vertical Tabs.
  const [openRow, setOpenRow] = useState<string | null>(null);
  const syncedTab = useRef<string | null>(null);
  // Holds the tapped row in place while the previously open one collapses.
  const anchorRow = useAccordionScrollAnchor();

  // Picking a phase on the APT ring (or with the pager) reveals that row too.
  useEffect(() => {
    if (activeTab === syncedTab.current) return;
    const first = syncedTab.current === null;
    syncedTab.current = activeTab;
    if (!first) setOpenRow(activeTab);
  }, [activeTab]);

  const handleAccordionClick = (value: string) => {
    if (openRow === value) {
      setOpenRow(null);
      return;
    }
    setOpenRow(value);
    setActiveTab(value);
  };

  // Picking a phase outside the accordion (ring circle, desktop step list) both
  // selects it and reveals its row, so the ring below `lg` never highlights a
  // phase whose copy is collapsed — including the very first pick, which the
  // effect above cannot see when it re-selects the initial phase.
  const handlePhaseSelect = (value: string) => {
    setActiveTab(value);
    setOpenRow(value);
  };

  const activeStepIndex = APT_STEPS.findIndex((step) => step.value === activeTab);
  const activeStep = APT_STEPS[activeStepIndex] ?? APT_STEPS[0];
  const canGoPrev = activeStepIndex > 0;
  const canGoNext = activeStepIndex < APT_STEPS.length - 1;

  const handlePrev = () => {
    if (canGoPrev) setActiveTab(APT_STEPS[activeStepIndex - 1].value);
  };
  const handleNext = () => {
    if (canGoNext) setActiveTab(APT_STEPS[activeStepIndex + 1].value);
  };

  return (
    <div className="relative w-full px-4 sm:px-6 lg:px-8 pt-2">
      <div className="relative max-w-[1300px] mx-auto">
        {/* Hero: intro text (left) + APT diagram (right, V1 build). The tall
            min-height reserves room for the diagram so the step section drops
            below it, matching the V1 build's padding-top: 440px separation. */}
        <div className="relative lg:min-h-[700px]">
          {/* Intro text */}
          <div className="relative z-10 max-w-[600px]">
            <ScrollReveal direction="up" delay={0} className="relative z-10">
              <div className="flex flex-col gap-6 sm:gap-7 text-text-highlight">
                <p className="font-[var(--font-avenir)] text-[16px] leading-[28px] tracking-[0.5px] text-justify">
                  Adversary Simulation is an advanced assessment service that takes customer one
                  step further from penetration test. In traditional penetration test, the
                  consultant normally provides theoretical and practical view of vulnerabilities,
                  relevant exploits, and whether the targets predefined by the customer can be
                  compromised. It helps provide the customer quick view of the security posture and
                  true risk of the targeted systems; and thereby; prioritize related remediation
                  efforts.
                </p>
                <p className="font-[var(--font-avenir)] text-[16px] leading-[28px] tracking-[0.5px] text-justify">
                  In ECQ Adversary Simulation service, the assessment is not limited to a predefined
                  target or methodology or techniques, but rather the goal of an adversary to the
                  business of the customer.
                </p>
              </div>
            </ScrollReveal>
          </div>

          {/* APT diagram — inline below text on mobile; absolute on the right
              on desktop, reproducing the V1 circuit background + ring of
              phase circles around the red "APT" core. */}
          <div className="apt-diagram">
            <div className="apt-diagram__stage">
              {/* eslint-disable-next-line @next/next/no-img-element -- decorative
                  full-bleed SVG background; next/image does not optimize SVG. */}
              <img
                src="/images/service/offend/adversary-simulation/adversary-apt-background.svg"
                alt=""
                aria-hidden="true"
                className="apt-diagram__bg"
              />
              <div className="apt-diagram__ring">
                {DIAGRAM_CIRCLES.map((circle, index) => (
                  <button
                    key={circle.value}
                    type="button"
                    onClick={() => handlePhaseSelect(circle.value)}
                    aria-pressed={isDesktop ? circle.value === activeTab : circle.value === openRow}
                    // Both classes ride along; the stylesheet picks which one
                    // paints the red ring at each breakpoint (see below).
                    className={`apt-diagram__circle${
                      circle.value === activeTab ? " apt-diagram__circle--active" : ""
                    }${circle.value === openRow ? " apt-diagram__circle--open" : ""}`}
                    style={{
                      top: circle.top,
                      left: circle.left,
                      // Staggered pop-in like V1 (.adversary-apt__circle
                      // animation-delay: 0.2s .. 1.4s).
                      animationDelay: `${(index + 1) * 0.2}s`,
                    }}
                  >
                    <span>{circle.label}</span>
                  </button>
                ))}
                <div className="apt-diagram__title">
                  <span>APT</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step navigation + content panel */}
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[minmax(0,438px)_minmax(0,700px)] gap-8 lg:gap-16 pb-16 mt-[60px]">
          {/* Desktop phase list */}
          <div data-steplist="desktop" className="relative hidden lg:flex lg:flex-col">
            {/* No continuous rule behind the list: each phase carries its own
                45px divider, so the 15px gaps between them stay empty rather
                than showing a faint grey line through them. */}
            {APT_STEPS.map((step, index) => {
              const isActive = step.value === activeTab;
              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => handlePhaseSelect(step.value)}
                  className={`group relative flex items-center w-full min-h-[45px] pt-2 mb-[15px] border-r transition-colors ${
                    isActive ? "border-primary" : "border-[#78797D] hover:border-primary"
                  }`}
                >
                  {/* V1 sets `font-family: "AvenirNext"` (AvenirNext-Bold) on
                      `.resources-brochures__number`, so the number is always
                      bold while the step name stays regular. */}
                  <span
                    className={`w-[39px] shrink-0 text-left font-[var(--font-avenir)] font-bold text-[20px] leading-[1.4] transition-colors ${
                      isActive ? "text-primary" : "text-[#78797D] group-hover:text-primary"
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span
                    className={`font-[var(--font-avenir)] text-[20px] leading-[1.4] whitespace-nowrap transition-colors ${
                      isActive ? "text-primary" : "text-white group-hover:text-primary"
                    }`}
                  >
                    {step.name}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Mobile accordion — shared look with Core Capabilities and the
              vertical `Tabs` step lists (see `common/accordion.tsx`). Opens
              fully collapsed and the open row is what the APT ring highlights,
              so an untouched page shows no phase as selected. The "Page NN"
              pager indicator stays desktop-only. */}
          <div data-accordion="steps" className={`${accordionCard} flex flex-col lg:hidden`}>
            {APT_STEPS.map((step, index) => {
              const isOpen = openRow === step.value;
              const headerId = `${uid}-phase-${step.id}`;
              const panelId = `${uid}-phasepanel-${step.id}`;

              return (
                <React.Fragment key={step.id}>
                  <button
                    type="button"
                    id={headerId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={(event) => {
                      anchorRow(event.currentTarget);
                      handleAccordionClick(step.value);
                    }}
                    className={`${accordionHeader} ${index > 0 ? accordionDivider : ""} ${
                      isOpen ? "text-primary" : "text-text-default hover:text-text-highlight"
                    }`}
                  >
                    <span
                      className={`w-[22px] shrink-0 font-bold transition-colors duration-200 ${
                        isOpen ? "text-primary" : "text-[#78797D]"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="flex-1 min-w-0">{step.name}</span>
                    <AccordionChevron open={isOpen} />
                  </button>

                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={headerId}
                    inert={!isOpen}
                    className={`${accordionPanel} ${
                      isOpen ? accordionPanelOpen : accordionPanelClosed
                    }`}
                    style={{ background: ACCORDION_PANEL_BG }}
                  >
                    <div className={accordionPanelClip}>
                      <div
                        className={`${accordionBody} flex flex-col gap-5 ${
                          isOpen ? accordionBodyOpen : accordionBodyClosed
                        }`}
                      >
                        {step.description.split("\n\n").map((paragraph, i) => (
                          <p key={i}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Content panel (desktop; mobile shows it inline per accordion row) */}
          <div className="hidden lg:flex flex-col gap-4">
            <div className="flex items-end justify-between gap-4">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="font-[var(--font-avenir)] text-[12px] leading-[20px] tracking-[0.25px] text-[#78797D]">
                    Page
                  </span>
                  <span className="font-[var(--font-avenir)] text-[12px] leading-[20px] tracking-[0.5px] text-primary">
                    {String(activeStepIndex + 1).padStart(2, "0")}
                  </span>
                </div>
                <p className="font-[var(--font-avenir)] text-[20px] leading-none text-primary">
                  {activeStep.name}
                </p>
              </div>

              {/* Pager — V1 uses boxed #1B2026 prev/next chevrons (no download). */}
              <div className="flex items-center gap-[5px]">
                <button
                  type="button"
                  aria-label="Previous step"
                  onClick={handlePrev}
                  disabled={!canGoPrev}
                  className="size-[30px] rounded-[3px] bg-[#1B2026] flex items-center justify-center text-white hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <svg
                    width="8"
                    height="12"
                    viewBox="0 0 7 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.00684 5.46191L5.95508 9.41016L5.24805 10.1172L0.723633 5.59375L0.838867 5.47852L0.707031 5.34766L5.34766 0.707031L6.05469 1.41406L2.00684 5.46191Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  aria-label="Next step"
                  onClick={handleNext}
                  disabled={!canGoNext}
                  className="size-[30px] rounded-[3px] bg-[#1B2026] flex items-center justify-center text-white hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <svg
                    width="8"
                    height="12"
                    viewBox="0 0 7 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.75488 5.3623L0.806641 1.41406L1.51367 0.707031L6.03809 5.23047L5.92285 5.3457L6.05469 5.47656L1.41406 10.1172L0.707031 9.41016L4.75488 5.3623Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* V1 runs this card as a slick slider (`resources-brochusres__slider`)
                at its defaults: the whole track slides sideways in 300ms `ease`,
                and the track keeps the height of the longest phase
                (`items-start`, so each card still ends with its own copy) instead
                of resizing as phases change.
                Same fill and no drop shadow, so this panel and the mobile
                accordion read as one surface. */}
            <div className="overflow-hidden">
              <div
                className="flex items-start transition-transform duration-300 ease-[ease] motion-reduce:transition-none"
                style={{ transform: `translateX(-${activeStepIndex * 100}%)` }}
              >
                {APT_STEPS.map((step, index) => (
                  <div
                    key={step.value}
                    aria-hidden={index !== activeStepIndex}
                    className="w-full shrink-0 px-[25px] py-5 rounded-[4px]"
                    style={{ background: ACCORDION_PANEL_BG }}
                  >
                    <div className="flex flex-col gap-[24px]">
                      {step.description.split("\n\n").map((paragraph, i) => (
                        <p
                          key={i}
                          className="font-[var(--font-avenir)] text-[18px] leading-[27px] text-white text-justify"
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        /* ---- APT diagram (faithful V1 reproduction) ---- */
        /* Mobile: the V1 artwork is a wide 1400x1539 circuit board with the
           ring sitting off to the right, so scaling the whole board down to
           phone width leaves a ~96px ring whose phase labels collide. Crop to
           the ring instead: blow the stage up to 232cqw and offset it so the
           ring plus its outer circles sit centred in a square viewport. */
        .apt-diagram {
          position: relative;
          container-type: inline-size;
          pointer-events: none;
          margin: 32px auto 0;
          max-width: 460px;
          aspect-ratio: 1 / 1;
          overflow: hidden;
        }
        .apt-diagram__stage {
          position: absolute;
          top: -74.2cqw;
          left: -117.8cqw;
          width: 232.4cqw;
        }
        /* Desktop: absolute full-container-width layer behind the intro text,
           so the ring falls on the right exactly like the V1 build. */
        @media (min-width: 1024px) {
          .apt-diagram__stage {
            position: relative;
            top: auto;
            left: auto;
            width: 100%;
          }
          .apt-diagram {
            position: absolute;
            top: -291px;
            left: 0;
            right: 0;
            width: auto;
            max-width: none;
            margin: 0;
            aspect-ratio: auto;
            overflow: visible;
            z-index: 0;
            /* Fade out the empty upper texture so the diagram never paints over
               the layout breadcrumb / "APT Adversary" title / tabs above it. */
            -webkit-mask-image: linear-gradient(
              to bottom,
              transparent 0,
              transparent 280px,
              #000 380px,
              #000 100%
            );
            mask-image: linear-gradient(
              to bottom,
              transparent 0,
              transparent 280px,
              #000 380px,
              #000 100%
            );
          }
        }
        .apt-diagram__bg {
          display: block;
          width: 100%;
          height: auto;
        }
        /* V1 .adversary-apt__group-circle. It is its own query container so the
           circle text, borders and "APT" scale with the ring itself — the same
           values then hold on desktop and inside the zoomed mobile crop. */
        .apt-diagram__ring {
          position: absolute;
          top: 33%;
          left: 53%;
          width: 29.5%;
          height: 34.1%;
          container-type: inline-size;
        }
        /* V1 .adversary-apt__circle */
        .apt-diagram__circle {
          position: absolute;
          width: 26%;
          aspect-ratio: 1 / 1;
          border: 1.46cqw solid #15181d;
          background: #21262b;
          color: #ec1938;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          line-height: 1;
          pointer-events: auto;
          cursor: pointer;
          transition:
            transform 0.25s ease,
            background 0.25s ease;
          /* V1 pop-in: scale up from teal, morph to dark + red text. The
             backwards fill keeps circles hidden through their stagger delay;
             the 100% keyframe matches the resting state so hover still works. */
          animation: adversaryCircle 1.7s backwards;
        }
        @keyframes adversaryCircle {
          0% {
            opacity: 0;
            transform: scale3d(0.3, 0.3, 0.3);
            background: #00c7cd;
            color: #21262b;
          }
          50% {
            opacity: 1;
            background: #00c7cd;
            color: #21262b;
          }
          100% {
            opacity: 1;
            transform: none;
            background: #21262b;
            color: #ec1938;
          }
        }
        .apt-diagram__circle span {
          font-family: var(--font-avenir);
          font-size: 5.83cqw;
        }
        .apt-diagram__circle:hover {
          background: #2b3138;
        }
        /* Selected phase — red ring only (no glow). Which phase counts as
           selected differs per breakpoint, so the choice lives here rather than
           in a JS media query (no ring flashing in after hydration):
             • >= lg the panel beside the list always shows a phase, so the ring
               follows that selection.
             • < lg the copy lives in the accordion, which opens fully collapsed
               — nothing is selected yet, so no circle is ringed. */
        @media (min-width: 1024px) {
          .apt-diagram__circle--active {
            border-color: #ec1938;
          }
        }
        @media (max-width: 1023.98px) {
          .apt-diagram__circle--open {
            border-color: #ec1938;
          }
        }
        /* V1 .adversary-apt__title (wow fadeIn) */
        .apt-diagram__title {
          position: absolute;
          top: 50%;
          left: 68%;
          transform: translate(-50%, -50%);
          color: #ec1938;
          font-family: var(--font-avenir);
          font-weight: 700;
          font-size: 14.58cqw;
          line-height: 1;
          pointer-events: none;
          animation: aptTitleFadeIn 1.1s ease-out 1.3s backwards;
        }
        @keyframes aptTitleFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .apt-diagram__circle,
          .apt-diagram__title {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
};

export default AdversarySimulation;
